import React, { Component } from "react";

import SearchAgvanceField from "~/action-panel/components/common/search-agvance-field";
import { adminData } from "~/admin/data";
import { AutoSearch, DialogBox, DialogBoxFooterType, Loader, SelectInput } from "~/core";
import { ACTIVE_YN } from "~/core/picklist";
import { createAddLinkLabelText } from "~/i18n-messages";
import { GeometryUtils, SearchAPI, ToolConfig, Toolset } from "@ai360/core";
import { FieldResurfacePrompt } from "~/action-panel/components/common";
import { optionRendererWithCustomTitle } from "~/action-panel/components/common/validation-utils";
import { fetchOccupiedFarmNames } from "~/utils/api/field";
import _ from "lodash";

import { messages } from "../../../i18n-messages";
import { IImportFieldList } from "~/action-panel/components/import-module/components/import-wizard/models";

export interface IAddEditFieldProps {
    record: IImportFieldList;
    currentFieldData: any;
    drawToolFlags: any;
    fetchCurrentFieldData: (fieldGuid: string) => void;
    ignoreFarm: boolean;
    isLoading: boolean;
    isLockCustomer: boolean;
    isUpdateField?: boolean;
    locked: boolean;
    onAddNewCustomer: (customerName: string) => void;
    onClose: (isCancel: boolean) => void;
    onSave: (data: any, callback: () => void) => void;
    resetCurrentFieldData: () => void;
    removeImportFieldBoundary: () => void;
    toolsetPayload: any;
    fieldEditGeometries: any[];
    fieldEditsChanged: boolean;
    maximumArea: number;
    setIsLoading: (isLoading: boolean) => void;
    setActiveToolset: (toolset: any, payload?: any) => void;
    setActiveToolsetPayloadOnly: (payload: any) => void;
    setToolsetDisabled: (isDisabled: boolean) => void;
    intl: any;
    agvanceFieldList: any[];
    agvanceOrgLevelList: any[];
    fetchAgvanceFieldList: (customerGuid: string) => void;
    fetchAgvanceOrgLevelList: (customerGuid: string) => void;
    isAgvanceCustomer: boolean;
    userGuid: string;
}

interface IState {
    customers: SearchAPI.ILegacyCustomerResult[];
    editFarmName: string;
    editFieldName: string;
    editFieldGuid: string;
    ignoreFarm: boolean;
    agvanceCustomerGuid: string;
    agvanceFieldGuid: string;
    agvanceFieldList: any[];
    agvanceFieldOrgLevelGuid: string;
    agvanceOrgList: any[];
    customerFieldOptions: any[];
    customerFarmOptions: any[];
    editCustomerGuid: string;
    editCustomerName: string;
    fieldToSave: any;
    isConfirmationModalOpen: boolean;
    isMaxAreaModalOpen: boolean;
    isMinAreaModalOpen: boolean;
    openAddCustomerPanel: boolean;
    resetOptions: boolean;
    searchValue: string;
    showResurfacePrompt: boolean;
    yesAction: any;
    doneLoading: boolean;
    delaySelectedValue: boolean;
}

export class AddEditFieldModal_ extends Component<IAddEditFieldProps, IState> {
    static defaultProps = {
        locked: false,
    };

    constructor(props: IAddEditFieldProps) {
        super(props);
        const {
            customerGuid,
            customerName,
            farmName,
            fieldName,
            fieldGuid,
            agvanceCustomerGuid,
            agvanceFieldGuid,
            agvanceFieldOrgLevelGuid,
        } = props.record;

        if (customerGuid && props.isAgvanceCustomer) {
            this.props.fetchAgvanceOrgLevelList(customerGuid);
        }

        this.updateCustomer = this.updateCustomer.bind(this);
        this.onSaveField = this.onSaveField.bind(this);
        this.onResurfaceClose = this.onResurfaceClose.bind(this);
        this.onResurfaceCancel = this.onResurfaceCancel.bind(this);
        this.noOptionsRenderer = this.noOptionsRenderer.bind(this);
        this.onSearchChange = this.onSearchChange.bind(this);
        this.updateFarmName = this.updateFarmName.bind(this);
        this.updateFieldName = this.updateFieldName.bind(this);

        this.state = {
            customers: [],
            agvanceCustomerGuid,
            agvanceFieldGuid,
            agvanceFieldList: [],
            agvanceFieldOrgLevelGuid,
            agvanceOrgList: [],
            customerFarmOptions: [],
            customerFieldOptions: [],
            editCustomerGuid: null,
            editCustomerName: customerName,
            editFarmName: props.ignoreFarm ? "" : farmName,
            editFieldGuid: fieldGuid,
            editFieldName: fieldName,
            fieldToSave: null,
            ignoreFarm: props.ignoreFarm,
            isConfirmationModalOpen: false,
            isMaxAreaModalOpen: false,
            isMinAreaModalOpen: false,
            openAddCustomerPanel: false,
            searchValue: "",
            showResurfacePrompt: false,
            resetOptions: false,
            yesAction: null,
            doneLoading: false,
            delaySelectedValue: true,
        };
    }

    async componentDidMount() {
        const { customerGuid, customerName, fieldGuid, fieldName, farmName } = this.props.record;
        const { toolsetPayload, userGuid, isLockCustomer, isUpdateField } = this.props;

        this.props.setActiveToolset(Toolset.DRAW_EDIT_IMPORT, toolsetPayload);
        if (fieldGuid) {
            this.props.fetchCurrentFieldData(fieldGuid);
        } else {
            this.props.resetCurrentFieldData();
        }
        if (this.props.isAgvanceCustomer && customerGuid) {
            this.props.fetchAgvanceFieldList(customerGuid);
        }

        const customersPromise = SearchAPI.getCustomers({
            userGuid,
            enrolled: isLockCustomer ? true : null,
            lastUsedCustomer: SearchAPI.LastUsedCustomer.Match,
        });
        const occupiedFarmNamesPromise =
            customerGuid == null || customerGuid === ""
                ? Promise.resolve<string[]>([])
                : fetchOccupiedFarmNames(customerGuid, true, userGuid);

        const customers = await customersPromise;
        const occupiedFarmNames = await occupiedFarmNamesPromise;
        const customerFarmOptions = occupiedFarmNames
            .map((farm) => ({
                value: farm,
                label: farm,
            }))
            .concat({
                value: farmName || "",
                label: farmName || "",
            });
        if (isUpdateField) {
            this.updateFieldState(customerGuid, customerName, farmName, fieldName, userGuid);
        }
        this.setState({
            customers,
            customerFarmOptions,
            editCustomerGuid: this.props.record.customerGuid,
            doneLoading: true,
        });
    }

    componentDidUpdate(prevProps: Readonly<IAddEditFieldProps>, prevState: Readonly<IState>): void {
        if (prevState.customers !== this.state.customers) {
            this.setState({
                resetOptions: true,
            });
        }
        if (this.props.agvanceOrgLevelList !== prevProps.agvanceOrgLevelList) {
            this.setAgvanceCustomer(this.props.agvanceOrgLevelList);
            this.setState({
                agvanceOrgList: this.props.agvanceOrgLevelList,
            });
        }

        if (this.props.currentFieldData !== prevProps.currentFieldData) {
            const { agvanceFieldGuid, agvanceFieldOrgLevelGuid, agvanceCustomerGuid } =
                this.props.currentFieldData;

            this.setState({
                agvanceFieldGuid,
                agvanceFieldOrgLevelGuid,
                agvanceCustomerGuid,
            });
        }
        // Sets Agvance guids for the perfect matched field and farm name
        if (this.props.agvanceFieldList !== prevProps.agvanceFieldList) {
            const { editFarmName, editFieldName, ignoreFarm } = this.state;

            const selectedField = this.props.agvanceFieldList.filter(
                (item) => item.name === editFieldName && item.farmName === editFarmName
            );
            if (selectedField.length) {
                this.setState({
                    editFarmName: ignoreFarm ? "" : selectedField[0].farmName,
                    agvanceFieldGuid: selectedField[0].agvanceGuid,
                });
            }

            this.setState({
                agvanceFieldList: this.props.agvanceFieldList,
            });
        }
    }

    private canSaveEdits() {
        const { fieldEditGeometries, removeImportFieldBoundary } = this.props;
        const { editCustomerGuid, editFieldName } = this.state;

        return (
            editCustomerGuid !== "" &&
            editFieldName !== "" &&
            fieldEditGeometries &&
            // Allow save when there are no field geometries if
            // in import wizard and import file removal is needed
            (fieldEditGeometries.length > 0 || removeImportFieldBoundary)
        );
    }

    private close(isCancel?: boolean) {
        const { onClose, setIsLoading, setActiveToolset } = this.props;

        this.setState({
            editCustomerGuid: "",
            editCustomerName: "",
            editFarmName: "",
            editFieldName: "",
            customerFarmOptions: [],
        });
        setIsLoading(false);
        setActiveToolset(Toolset.DEFAULT);
        onClose(isCancel);
    }

    private save(resurfaceEventResult?: any) {
        const {
            fieldEditGeometries,
            fieldEditsChanged,
            setIsLoading,
            onSave,
            setToolsetDisabled,
            currentFieldData,
        } = this.props;
        const { editCustomerGuid, editCustomerName, editFarmName, editFieldName } = this.state;

        setIsLoading(true);
        let data = {
            editCustomerGuid,
            editCustomerName,
            editFarmName,
            editFieldName,
            fieldEditGeometries,
            fieldEditsChanged,
            ...currentFieldData,
        };

        if (resurfaceEventResult) {
            data.resurfaceEventResult = resurfaceEventResult;
        }

        if (this.props.isAgvanceCustomer) {
            const { agvanceFieldGuid, agvanceFieldOrgLevelGuid, agvanceCustomerGuid } = this.state;
            data = {
                ...data,
                agvanceFieldGuid,
                agvanceFieldOrgLevelGuid,
                agvanceCustomerGuid,
            };
        }
        setToolsetDisabled(true);
        onSave(data, () => this.close());
    }

    private setAgvanceCustomer = (agvanceOrgList: any[]) => {
        const { agvanceCustomerGuid, agvanceFieldOrgLevelGuid } = this.state;
        const selectedValue = agvanceOrgList.filter(({ isPrimary }) => isPrimary);
        if (!agvanceCustomerGuid && !agvanceFieldOrgLevelGuid && selectedValue.length) {
            this.setState({
                agvanceFieldOrgLevelGuid: selectedValue[0].orgLevelGuid,
                agvanceCustomerGuid: selectedValue[0].agvanceCustomerGuid,
            });
        }
    };

    private hideAgvanceCustomers() {
        return (
            !this.props.isAgvanceCustomer ||
            !this.state.editCustomerGuid ||
            this.state.agvanceOrgList.length === 1
        );
    }

    private showConfirmDialog(yesAction) {
        this.setState({
            isConfirmationModalOpen: true,
            yesAction,
        });
    }

    private showMaxAreaDialog() {
        this.setState({
            isMaxAreaModalOpen: true,
        });
    }

    private showMinAreaDialog() {
        this.setState({
            isMinAreaModalOpen: true,
        });
    }

    private closeConfirmDialog() {
        this.setState({
            isConfirmationModalOpen: false,
            yesAction: null,
        });
    }

    private closeMaxAreaDialog() {
        this.setState({
            isMaxAreaModalOpen: false,
        });
    }

    private closeMinAreaDialog() {
        this.setState({
            isMinAreaModalOpen: false,
        });
    }

    private confirmYes() {
        if (this.state.yesAction) {
            this.state.yesAction();
        }
        this.closeConfirmDialog();
        this.close();
    }

    private confirmMinAreaYes() {
        this.showResurfacePromptOrSave();
        this.closeMinAreaDialog();
    }

    private showResurfacePromptOrSave() {
        if (this.isEditingField() && !this.props.isUpdateField) {
            this.showResurfacePrompt();
        } else {
            this.save();
        }
    }

    private updateCustomer(customer: SearchAPI.ILegacyCustomerResult) {
        if (!customer) {
            this.setState({
                editCustomerGuid: "",
                editCustomerName: "",
                customerFarmOptions: [],
            });

            if (this.props.isAgvanceCustomer) {
                this.setState({
                    agvanceFieldOrgLevelGuid: "",
                    agvanceCustomerGuid: "",
                });
            }
        } else if (customer.customerGuid !== this.state.editCustomerGuid) {
            const { customerGuid, customerName } = customer;
            const { isUpdateField, userGuid } = this.props;
            fetchOccupiedFarmNames(customerGuid, true, this.props.userGuid).then((farmNames) => {
                if (isUpdateField) {
                    this.updateFarmState(farmNames, this.state.editFarmName);
                }
                this.setState({
                    editCustomerGuid: customerGuid,
                    editCustomerName: customerName,
                    customerFarmOptions: farmNames.map((farm) => ({
                        label: farm,
                        value: farm,
                    })),
                });
            });
            if (this.props.isAgvanceCustomer) {
                this.props.fetchAgvanceFieldList(customerGuid);
                this.props.fetchAgvanceOrgLevelList(customerGuid);
            }
            if (this.props.isUpdateField) {
                this.updateFieldState(
                    customerGuid,
                    customerName,
                    null,
                    this.state.editFieldName,
                    userGuid
                );
            }
        }
        const { toolsetPayload, isUpdateField } = this.props;
        this.updateToolsetPayload(
            !customer ? this.props.toolsetPayload.customerGuid : customer.customerGuid,
            toolsetPayload.fieldGuid && isUpdateField
                ? this.props.toolsetPayload.fieldGuid
                : undefined
        );
    }

    private updateFarmName(editFarmName: string) {
        const { editCustomerGuid, editCustomerName, editFieldName } = this.state;
        const { isUpdateField, userGuid } = this.props;
        if (isUpdateField) {
            this.updateFieldState(
                editCustomerGuid,
                editCustomerName,
                editFarmName,
                editFieldName,
                userGuid
            );
        }
        this.setState({
            editFarmName,
        });
    }

    private async updateFarmState(farmResults: any[], farmName: string) {
        const resultsToConsider = farmResults.filter((x) => x != null && x !== "");
        const farmsToConsider = resultsToConsider.filter((x) => !farmName || x === farmName);
        this.setState({
            editFarmName:
                farmsToConsider.length === 1
                    ? farmsToConsider[0]
                    : farmResults.length === 0
                    ? farmName
                    : "",
        });
    }

    private async updateFieldState(
        customerGuid: string,
        customerName: string,
        farmName: string,
        fieldName: string,
        userGuid: string
    ) {
        const results =
            customerName != ""
                ? await SearchAPI.getFields({
                      customerGuid: customerGuid !== "" ? [customerGuid] : null,
                      customerName,
                      active: true,
                      userGuid,
                  })
                : [];
        const resultsToConsider = results.filter((x) => x.name != null && x.name !== "");
        const customerFieldOptions = _(resultsToConsider)
            .filter((x) => !farmName || x.farmName === farmName)
            .uniqBy((x) => x.name)
            .map((x) => ({
                label: x.name,
                value: x.id,
            }))
            .value();
        const fieldsToConsider = resultsToConsider
            .filter((x) => !farmName || x.farmName === farmName)
            .filter((x) => !fieldName || x.name === fieldName);
        this.setState({
            customerFieldOptions,
            editFieldName:
                fieldsToConsider.length === 1
                    ? fieldsToConsider[0].name
                    : results.length === 0
                    ? fieldName
                    : "",
        });
    }

    private updateFieldName(
        editFieldName: string,
        agvanceFieldGuid = "",
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        fieldGuid: string = undefined,
        farmName: string = undefined
    ) {
        if (this.props.isAgvanceCustomer) {
            // Set the values for agvance integration
            this.setState({
                agvanceFieldGuid,
                editFarmName: this.state.ignoreFarm ? "" : farmName ?? this.state.editFarmName,
            });
        }
        if (this.props.isUpdateField) {
            this.setState({
                editFieldGuid: editFieldName,
                editFieldName: this.state.customerFieldOptions.find(
                    (option) => option.value === editFieldName
                )?.label,
            });
        } else {
            this.setState({
                editFieldName,
            });
        }
    }

    private onAgvanceCustomerChange(guid: string) {
        const selectedItem = this.props.agvanceOrgLevelList.find((record) => {
            return record.agvanceCustomerGuid === guid;
        });
        if (selectedItem) {
            this.setState({
                agvanceCustomerGuid: selectedItem.agvanceCustomerGuid,
                agvanceFieldOrgLevelGuid: selectedItem.orgLevelGuid,
                editFarmName: "",
                editFieldName: "",
            });
        } else {
            this.setState({
                agvanceFieldOrgLevelGuid: "",
                agvanceCustomerGuid: "",
            });
        }
    }

    private updateToolsetPayload(customerGuid: string, fieldGuid = "") {
        this.props.setActiveToolsetPayloadOnly({
            ...this.props.toolsetPayload,
            customerGuid,
            fieldGuid,
        });
    }

    private getAgvanceFieldList() {
        return this.state.agvanceFieldList?.filter(({ agvanceGrowerGuid }) => {
            return this.state.agvanceCustomerGuid === agvanceGrowerGuid;
        });
    }

    private noOptionsRenderer() {
        const { formatMessage } = this.props.intl;
        const { editCustomerName, searchValue } = this.state;
        const customerName = searchValue ? searchValue : editCustomerName;
        return (
            <div className="add-customer" onClick={() => this.props.onAddNewCustomer(customerName)}>
                {createAddLinkLabelText(formatMessage, messages.customer)}
            </div>
        );
    }

    private onSaveField() {
        const { fieldEditGeometries, maximumArea } = this.props;

        if (GeometryUtils.checkMinAcreage(fieldEditGeometries)) {
            this.showMinAreaDialog();
            return;
        } else if (GeometryUtils.checkMaxAcreage(fieldEditGeometries, maximumArea)) {
            this.showMaxAreaDialog();
            return;
        } else if (fieldEditGeometries.length === 0) {
            this.showConfirmDialog(this.props.removeImportFieldBoundary);
            return;
        }
        this.showResurfacePromptOrSave();
    }

    private isEditingField() {
        return this.props.record && this.props.record.fieldGuid;
    }

    private onResetOptions = () => {
        this.setState({
            resetOptions: false,
        });
    };

    private onSearchChange(searchValue: string) {
        this.setState({ searchValue });
    }

    private showResurfacePrompt() {
        this.setState({ showResurfacePrompt: true });
    }

    private onResurfaceClose(resurfaceEventResult) {
        this.setState({ showResurfacePrompt: false }, () => this.save(resurfaceEventResult));
    }

    private onResurfaceCancel() {
        this.setState({ showResurfacePrompt: false });
    }

    render() {
        const {
            editCustomerGuid,
            editFarmName,
            editFieldGuid,
            editFieldName,
            agvanceOrgList,
            agvanceCustomerGuid,
            doneLoading,
            delaySelectedValue,
        } = this.state;
        const { record, locked, isLoading, maximumArea, isUpdateField } = this.props;
        const { customerName } = record;
        const { formatMessage } = this.props.intl;
        const { customers } = this.state;
        const minAcres = ToolConfig.minimumAcreage;
        const initFilter = customers.find((item) => item.customerName === customerName)
            ? customerName
            : "";

        const fieldResurfacePromptProps = {
            fieldGuid: record.fieldGuid,
            isOpen: this.state.showResurfacePrompt,
            onCancel: this.onResurfaceCancel,
            onClose: this.onResurfaceClose,
        };

        if (doneLoading && delaySelectedValue) {
            this.setState({
                delaySelectedValue: false,
            });
        }

        return (
            <div>
                <div className="edit-modal-container">
                    <DialogBox
                        action="save"
                        actionDisabled={!this.canSaveEdits()}
                        className="edit-field-modal"
                        closeOnClickOff={false}
                        closeOnEscape={false}
                        draggable
                        footerType={DialogBoxFooterType.ACTION_CANCEL}
                        forceOverflow
                        hideCloseX
                        isOpen
                        isModal
                        onAction={this.onSaveField}
                        onClose={() => this.close(true)}
                        title={formatMessage(messages.addEditFieldBoundaryText, {
                            count: record.fieldGuid || record.importFieldBoundaryGuidList ? 1 : 0,
                        })}
                    >
                        {isLoading ? <Loader /> : null}
                        <div className="edit-field-modal-body">
                            <AutoSearch
                                initialFilterStr={initFilter}
                                required={true}
                                disabled={locked}
                                itemList={customers}
                                selectedValue={delaySelectedValue ? null : editCustomerGuid}
                                onSelection={this.updateCustomer}
                                placeholderText={formatMessage(messages.customerName)}
                                keyProp={SearchAPI.Props.CUSTOMER_GUID}
                                nameProp="customerName"
                                secondaryPropList={["city", "state"]}
                                titlePropList={["customerName", "street", "city", "state"]}
                                noOptionsRenderer={this.noOptionsRenderer}
                                onResetOptions={this.onResetOptions}
                                onSearchChange={this.onSearchChange}
                                resetOptions={this.state.resetOptions}
                                autoFocus
                                openOnFocus={false}
                            />
                            {this.hideAgvanceCustomers() ? null : (
                                <SelectInput
                                    disabled={locked || this.state.ignoreFarm}
                                    optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                    clearFilterInputOnBlur={false}
                                    placeholderText={formatMessage(messages.agvanceCustomer)}
                                    value={agvanceCustomerGuid}
                                    onChange={(v) => this.onAgvanceCustomerChange(v)}
                                    options={agvanceOrgList.map(
                                        ({
                                            agvanceGrowerName,
                                            agvanceCustomerGuid,
                                            orgLevelName,
                                            alternateCustomerIdSelecting,
                                        }) => ({
                                            label: `${agvanceGrowerName} > ${orgLevelName}`,
                                            titleValue: `${agvanceGrowerName} - ${alternateCustomerIdSelecting}`,
                                            value: agvanceCustomerGuid,
                                        })
                                    )}
                                    optionRenderer={optionRendererWithCustomTitle}
                                    required
                                />
                            )}
                            <SelectInput
                                optionIsHiddenKey={ACTIVE_YN}
                                disabled={locked || this.state.ignoreFarm}
                                clearFilterInputOnBlur={false}
                                options={this.state.customerFarmOptions}
                                onChange={this.updateFarmName}
                                onInputChange={this.updateFarmName}
                                placeholderText={formatMessage(messages.farmNamePlaceholderText)}
                                value={editFarmName}
                                noOptionsRenderer={null}
                                initialFilterStr={editFarmName}
                                allowEmptyOptions
                            />
                            {isUpdateField ? (
                                <SelectInput
                                    optionIsHiddenKey={ACTIVE_YN}
                                    clearFilterInputOnBlur={false}
                                    options={this.state.customerFieldOptions}
                                    onChange={this.updateFieldName.bind(this)}
                                    placeholderText={formatMessage(
                                        messages.fieldNamePlaceholderText
                                    )}
                                    value={editFieldGuid}
                                    noOptionsRenderer={null}
                                    initialFilterStr={editFieldName}
                                    required={true}
                                />
                            ) : (
                                <SearchAgvanceField
                                    value={editFieldName}
                                    onChange={this.updateFieldName}
                                    onInputChange={this.updateFieldName}
                                    records={this.getAgvanceFieldList()}
                                    disabled={locked}
                                />
                            )}
                            <div className="edit-field-modal-body-message">
                                <div>
                                    {record.fieldGuid || record.importFieldBoundaryGuidList ? (
                                        <span>{formatMessage(messages.editBoundaryMessage)}</span>
                                    ) : (
                                        <div>
                                            <span className="red-star">*</span>
                                            <span>
                                                {formatMessage(messages.addBoundaryMessage)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </DialogBox>
                    <DialogBox
                        className="edit-field-area-warning"
                        title={formatMessage(messages.errorAboveMaxAcreageTitle)}
                        isOpen={this.state.isMaxAreaModalOpen}
                        onAction={() => this.closeMaxAreaDialog()}
                        onClose={() => this.closeMaxAreaDialog()}
                    >
                        {formatMessage(messages.errorAboveMaxAcreage, {
                            maxAcres: maximumArea,
                        })}
                    </DialogBox>

                    <DialogBox
                        title={formatMessage(messages.noValidPolygonsTitle)}
                        footerType={DialogBoxFooterType.YES_NO}
                        isOpen={this.state.isConfirmationModalOpen}
                        onAction={() => this.confirmYes()}
                        onClose={() => this.closeConfirmDialog()}
                    >
                        {formatMessage(messages.noValidPolygonsMsg)}
                    </DialogBox>
                </div>
                {record && record.fieldGuid && !isUpdateField && (
                    <FieldResurfacePrompt {...fieldResurfacePromptProps} />
                )}
                <DialogBox
                    title={formatMessage(messages.confirmSaveBelowTitle)}
                    footerType={DialogBoxFooterType.YES_NO}
                    isOpen={this.state.isMinAreaModalOpen}
                    onAction={() => this.confirmMinAreaYes()}
                    onClose={() => this.closeMinAreaDialog()}
                >
                    {formatMessage(messages.confirmSaveBelowMinAcreage, {
                        minAcres,
                    })}
                </DialogBox>
            </div>
        );
    }
}
